import React, { useState, useEffect, useCallback } from 'react';
import { ModuloApi } from '../../../api/ModuloApi';
import { TestApi } from '../../../api/TestApi';
import { PreguntaApi } from '../../../api/PreguntaApi';
import './AdminModulos.css';

const moduloApi   = new ModuloApi();
const testApi     = new TestApi();
const preguntaApi = new PreguntaApi();

const BLANK_MODULO = { modulo_name: '', modulo_description: '', modulo_large_description: '', modulo_url: '' };
const BLANK_Q      = { pregunta: '', opciona: '', opcionb: '', opcionc: '', answer: 'a' };

const AdminModulos = () => {
  const [modulos, setModulos]         = useState([]);
  const [editId, setEditId]           = useState(null);
  const [editForm, setEditForm]       = useState(BLANK_MODULO);
  const [addingNew, setAddingNew]     = useState(false);
  const [newForm, setNewForm]         = useState(BLANK_MODULO);
  const [expandedQ, setExpandedQ]     = useState(null);
  const [testMap, setTestMap]         = useState({});
  const [questions, setQuestions]     = useState({});
  const [qDraft, setQDraft]           = useState({});
  const [saving, setSaving]           = useState(false);
  const [toast, setToast]             = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const loadModulos = useCallback(async () => {
    try {
      const data = await moduloApi.getAllModulo();
      setModulos(data);
    } catch {
      showToast('Error cargando módulos', 'error');
    }
  }, []);

  useEffect(() => { loadModulos(); }, [loadModulos]);

  // ── Module CRUD ─────────────────────────────────────────────

  const startEdit = (m) => {
    setEditId(m.id);
    setEditForm({ modulo_name: m.modulo_name, modulo_description: m.modulo_description, modulo_large_description: m.modulo_large_description, modulo_url: m.modulo_url });
    setAddingNew(false);
  };

  const cancelEdit = () => { setEditId(null); setEditForm(BLANK_MODULO); };

  const saveEdit = async () => {
    if (!editForm.modulo_name.trim()) return showToast('El número de módulo es obligatorio', 'error');
    setSaving(true);
    try {
      await moduloApi.updateModulo(editId, editForm);
      setModulos(prev => prev.map(m => m.id === editId ? { ...m, ...editForm } : m));
      setEditId(null);
      showToast('Módulo guardado');
    } catch { showToast('Error guardando módulo', 'error'); }
    finally { setSaving(false); }
  };

  const saveNew = async () => {
    if (!newForm.modulo_name.trim()) return showToast('El número de módulo es obligatorio', 'error');
    setSaving(true);
    try {
      const added = await moduloApi.addModulo(newForm);
      setModulos(prev => [...prev, added]);
      setAddingNew(false);
      setNewForm(BLANK_MODULO);
      showToast('Módulo añadido');
    } catch { showToast('Error añadiendo módulo', 'error'); }
    finally { setSaving(false); }
  };

  const deleteModulo = async (m) => {
    if (!window.confirm(`¿Eliminar Módulo ${m.modulo_name}? Esta acción no se puede deshacer.`)) return;
    try {
      const test = await testApi.getTestByModuloId(m.id);
      if (test) {
        const qs = await preguntaApi.getPreguntasByIdTest(test.id);
        await Promise.all(qs.map(q => preguntaApi.deletePregunta(q.id)));
        await testApi.deleteTest(test.id);
      }
      await moduloApi.deleteModulo(m.id);
      setModulos(prev => prev.filter(x => x.id !== m.id));
      showToast('Módulo eliminado');
    } catch { showToast('Error eliminando módulo', 'error'); }
  };

  // ── Reorder (swap modulo_name with neighbour) ────────────────

  const moveModule = async (index, direction) => {
    const list  = [...modulos];
    const other = index + direction;
    if (other < 0 || other >= list.length) return;

    const a = list[index];
    const b = list[other];
    const tmpName = a.modulo_name;
    try {
      await moduloApi.updateModulo(a.id, { ...a, modulo_name: b.modulo_name });
      await moduloApi.updateModulo(b.id, { ...b, modulo_name: tmpName });
      list[index] = { ...a, modulo_name: b.modulo_name };
      list[other] = { ...b, modulo_name: tmpName };
      setModulos([...list].sort((x, y) => parseInt(x.modulo_name) - parseInt(y.modulo_name)));
    } catch { showToast('Error reordenando', 'error'); }
  };

  // ── Questions ────────────────────────────────────────────────

  const openQuestions = async (m) => {
    if (expandedQ === m.id) { setExpandedQ(null); return; }
    setExpandedQ(m.id);
    if (questions[m.id]) return;
    try {
      const test = await testApi.insertTest({ id_modulo: m.id });
      setTestMap(prev => ({ ...prev, [m.id]: test }));
      const qs = await preguntaApi.getPreguntasByIdTest(test.id);
      setQuestions(prev => ({ ...prev, [m.id]: qs }));
    } catch { showToast('Error cargando preguntas', 'error'); }
  };

  const addQDraft = (moduloId) => {
    setQDraft(prev => ({ ...prev, [moduloId]: { ...BLANK_Q } }));
  };

  const cancelQDraft = (moduloId) => {
    setQDraft(prev => { const n = { ...prev }; delete n[moduloId]; return n; });
  };

  const saveQDraft = async (moduloId) => {
    const draft = qDraft[moduloId];
    if (!draft?.pregunta?.trim()) return showToast('La pregunta no puede estar vacía', 'error');
    const test = testMap[moduloId];
    if (!test) return;
    try {
      const saved = await preguntaApi.insertPregunta({ ...draft, id_test: test.id });
      setQuestions(prev => ({ ...prev, [moduloId]: [...(prev[moduloId] || []), saved] }));
      cancelQDraft(moduloId);
      showToast('Pregunta añadida');
    } catch { showToast('Error guardando pregunta', 'error'); }
  };

  const updateQField = (moduloId, qId, field, value) => {
    setQuestions(prev => ({
      ...prev,
      [moduloId]: prev[moduloId].map(q => q.id === qId ? { ...q, [field]: value, _dirty: true } : q),
    }));
  };

  const saveQuestion = async (moduloId, q) => {
    try {
      await preguntaApi.updatePregunta(q.id, { pregunta: q.pregunta, opciona: q.opciona, opcionb: q.opcionb, opcionc: q.opcionc, answer: q.answer });
      setQuestions(prev => ({
        ...prev,
        [moduloId]: prev[moduloId].map(x => x.id === q.id ? { ...x, _dirty: false } : x),
      }));
      showToast('Pregunta guardada');
    } catch { showToast('Error guardando pregunta', 'error'); }
  };

  const deleteQuestion = async (moduloId, q) => {
    if (!window.confirm('¿Eliminar esta pregunta?')) return;
    try {
      await preguntaApi.deletePregunta(q.id);
      setQuestions(prev => ({ ...prev, [moduloId]: prev[moduloId].filter(x => x.id !== q.id) }));
      showToast('Pregunta eliminada');
    } catch { showToast('Error eliminando pregunta', 'error'); }
  };

  // ── Render ───────────────────────────────────────────────────

  const renderQField = (label, field, val, onChange) => (
    <div className="am-qfield">
      <label className="am-qlabel">{label}</label>
      <input className="am-qinput" value={val || ''} onChange={e => onChange(field, e.target.value)} />
    </div>
  );

  const renderEditForm = (form, onChange, onSave, onCancel, title) => (
    <div className="am-edit-form">
      <h3 className="am-edit-title">{title}</h3>
      <div className="am-fields">
        <div className="am-field">
          <label>Número de módulo</label>
          <input value={form.modulo_name} onChange={e => onChange('modulo_name', e.target.value)} placeholder="1" />
        </div>
        <div className="am-field">
          <label>Descripción breve</label>
          <input value={form.modulo_description} onChange={e => onChange('modulo_description', e.target.value)} placeholder="Introducción al método..." maxLength={80} />
        </div>
        <div className="am-field am-field--full">
          <label>Descripción completa</label>
          <textarea rows={3} value={form.modulo_large_description} onChange={e => onChange('modulo_large_description', e.target.value)} placeholder="Descripción larga del módulo..." maxLength={1000} />
        </div>
        <div className="am-field am-field--full">
          <label>URL del vídeo</label>
          <input value={form.modulo_url} onChange={e => onChange('modulo_url', e.target.value)} placeholder="https://..." />
        </div>
      </div>
      <div className="am-form-actions">
        <button className="am-btn am-btn--primary" onClick={onSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button className="am-btn am-btn--ghost" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );

  return (
    <div className="am-page">
      {toast && <div className={`am-toast am-toast--${toast.type}`}>{toast.msg}</div>}

      <div className="am-header">
        <div>
          <h1 className="am-title">Gestión de Módulos</h1>
          <p className="am-subtitle">{modulos.length} módulos · Edita, reordena y gestiona los tests</p>
        </div>
        <button className="am-btn am-btn--primary" onClick={() => { setAddingNew(true); setEditId(null); }}>
          + Nuevo módulo
        </button>
      </div>

      {addingNew && renderEditForm(
        newForm,
        (k, v) => setNewForm(p => ({ ...p, [k]: v })),
        saveNew,
        () => { setAddingNew(false); setNewForm(BLANK_MODULO); },
        'Añadir nuevo módulo'
      )}

      <div className="am-list">
        {modulos.map((m, idx) => (
          <div key={m.id} className={`am-card ${editId === m.id ? 'am-card--editing' : ''} ${expandedQ === m.id ? 'am-card--expanded' : ''}`}>
            <div className="am-card-row">
              <div className="am-order-btns">
                <button className="am-order-btn" onClick={() => moveModule(idx, -1)} disabled={idx === 0} title="Subir">▲</button>
                <button className="am-order-btn" onClick={() => moveModule(idx, 1)} disabled={idx === modulos.length - 1} title="Bajar">▼</button>
              </div>
              <div className="am-card-num">M{m.modulo_name}</div>
              <div className="am-card-info">
                <span className="am-card-name">{m.modulo_description || '(sin descripción)'}</span>
                {m.modulo_url && <span className="am-card-url" title={m.modulo_url}>🎬 {m.modulo_url.slice(0, 50)}{m.modulo_url.length > 50 ? '…' : ''}</span>}
              </div>
              <div className="am-card-actions">
                <button className="am-btn am-btn--sm" onClick={() => editId === m.id ? cancelEdit() : startEdit(m)}>
                  {editId === m.id ? 'Cerrar' : '✏️ Editar'}
                </button>
                <button className="am-btn am-btn--sm am-btn--gold" onClick={() => openQuestions(m)}>
                  📝 Preguntas
                </button>
                <button className="am-btn am-btn--sm am-btn--danger" onClick={() => deleteModulo(m)}>
                  🗑️
                </button>
              </div>
            </div>

            {editId === m.id && renderEditForm(
              editForm,
              (k, v) => setEditForm(p => ({ ...p, [k]: v })),
              saveEdit,
              cancelEdit,
              `Editando Módulo ${m.modulo_name}`
            )}

            {expandedQ === m.id && (
              <div className="am-questions">
                <h3 className="am-q-title">Preguntas del test — Módulo {m.modulo_name}</h3>
                {(questions[m.id] || []).length === 0 && !qDraft[m.id] && (
                  <p className="am-q-empty">Sin preguntas. Añade la primera.</p>
                )}
                {(questions[m.id] || []).map((q) => (
                  <div key={q.id} className={`am-q-card ${q._dirty ? 'am-q-card--dirty' : ''}`}>
                    <div className="am-q-row">
                      {renderQField('Pregunta', 'pregunta', q.pregunta, (f, v) => updateQField(m.id, q.id, f, v))}
                    </div>
                    <div className="am-q-options">
                      {renderQField('Opción A', 'opciona', q.opciona, (f, v) => updateQField(m.id, q.id, f, v))}
                      {renderQField('Opción B', 'opcionb', q.opcionb, (f, v) => updateQField(m.id, q.id, f, v))}
                      {renderQField('Opción C', 'opcionc', q.opcionc, (f, v) => updateQField(m.id, q.id, f, v))}
                      <div className="am-qfield">
                        <label className="am-qlabel">Respuesta correcta</label>
                        <select className="am-qinput" value={q.answer} onChange={e => updateQField(m.id, q.id, 'answer', e.target.value)}>
                          <option value="a">A</option>
                          <option value="b">B</option>
                          <option value="c">C</option>
                        </select>
                      </div>
                    </div>
                    <div className="am-q-actions">
                      {q._dirty && (
                        <button className="am-btn am-btn--sm am-btn--primary" onClick={() => saveQuestion(m.id, q)}>
                          Guardar cambios
                        </button>
                      )}
                      <button className="am-btn am-btn--sm am-btn--danger" onClick={() => deleteQuestion(m.id, q)}>
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}

                {qDraft[m.id] && (
                  <div className="am-q-card am-q-card--new">
                    <div className="am-q-row">
                      {renderQField('Pregunta', 'pregunta', qDraft[m.id].pregunta, (f, v) => setQDraft(p => ({ ...p, [m.id]: { ...p[m.id], [f]: v } })))}
                    </div>
                    <div className="am-q-options">
                      {['opciona','opcionb','opcionc'].map((key, i) => (
                        <div key={key} className="am-qfield">
                          <label className="am-qlabel">Opción {String.fromCharCode(65 + i)}</label>
                          <input className="am-qinput" value={qDraft[m.id][key] || ''} onChange={e => setQDraft(p => ({ ...p, [m.id]: { ...p[m.id], [key]: e.target.value } }))} />
                        </div>
                      ))}
                      <div className="am-qfield">
                        <label className="am-qlabel">Respuesta correcta</label>
                        <select className="am-qinput" value={qDraft[m.id].answer} onChange={e => setQDraft(p => ({ ...p, [m.id]: { ...p[m.id], answer: e.target.value } }))}>
                          <option value="a">A</option>
                          <option value="b">B</option>
                          <option value="c">C</option>
                        </select>
                      </div>
                    </div>
                    <div className="am-q-actions">
                      <button className="am-btn am-btn--sm am-btn--primary" onClick={() => saveQDraft(m.id)}>Guardar pregunta</button>
                      <button className="am-btn am-btn--sm am-btn--ghost" onClick={() => cancelQDraft(m.id)}>Cancelar</button>
                    </div>
                  </div>
                )}

                {!qDraft[m.id] && (
                  <button className="am-btn am-btn--add-q" onClick={() => addQDraft(m.id)}>
                    + Añadir pregunta
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminModulos;
